import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {RootState} from '@src/redux/store';
import {BASE_URL} from '@src/helpers/constants';
import {retrieveItem} from '@src/helpers/functions';
import * as TYPES from '@src/redux/types';

const GRAPHQL_QUERY = `
  query getReportList(
    $filter: reportFilterInput
    $data: reportExportInput
    $pagination: paginationInput
  ) {
    getReportList(filter: $filter, data: $data, pagination: $pagination) {
      columnList {
        key
        label
        type
      }
      items {
        readingId
        subjectId
        valueList
      }
      pagination {
        pageNumber
        pageLimit
        totalCount
      }
      exclusion {
        isApplied
        excludedQuestionCount
        excludedSubjectCount
        noFindingAnswerList
      }
      error {
        message
        statusCode
      }
    }
  }
`;

export type GetReportListParams = {
  filter?: TYPES.ReportFilterInput;
  data?: TYPES.ReportExportInput;
  pagination?: TYPES.PaginationInput;
};

const initialState: TYPES.ReportListState = {
  result: null,
  error: null,
  loading: false,
};

export const getReportListRequest = createAsyncThunk<
  TYPES.ReportListResponse,
  GetReportListParams,
  {extra: ApolloClient<NormalizedCacheObject>}
>('report/get_list', async (params: GetReportListParams) => {
  const sessionId = retrieveItem('sessionId') ?? '';
  try {
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        sessionId: sessionId,
      },
      body: JSON.stringify({
        operationName: 'getReportList',
        query: GRAPHQL_QUERY,
        variables: params ?? {},
      }),
    });
    const json = await response.json();
    if (json?.errors?.length) {
      const first = json.errors[0];
      return Promise.reject({
        message: first.message || 'Failed to load report',
        statusCode: String(response.status),
      });
    }
    return json?.data?.getReportList as TYPES.ReportListResponse;
  } catch (error) {
    const err = error as {message?: string; statusCode?: string};
    return Promise.reject({
      message: err.message || 'An error occurred',
      statusCode: err.statusCode || '500',
    });
  }
});

const getReportListSlice = createSlice({
  name: 'get_report_list',
  initialState: initialState,
  reducers: {
    getReportListClear: state => {
      state.result = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getReportListRequest.pending, state => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getReportListRequest.fulfilled, (state, action) => {
        state.result = action.payload;
        state.loading = false;
      })
      .addCase(getReportListRequest.rejected, (state, action) => {
        state.error =
          (action.payload as TYPES.Error) ||
          {message: 'Failed to fetch', statusCode: '500'};
        state.loading = false;
      });
  },
});

export const {getReportListClear} = getReportListSlice.actions;

export const getReportList = createSelector(
  (state: RootState) => state.report.getReportList.result,
  (state: RootState) => state.report.getReportList.error,
  (state: RootState) => state.report.getReportList.loading,
  (result, error, loading) => ({result, error, loading}),
);

export default getReportListSlice.reducer;
